'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { doorSvg } from '../drawing/door';
import { SvgArt } from '../drawing/SvgArt';
import { safeHex } from '../drawing/svg';
import {
  childLevel,
  headingLevel,
  items,
  SkLink,
  text,
  type HeadingLevel,
  type Localized,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';
import { doorStyleOr, hexOrUndefined, homeSurfaceClass, HomeSurfaceStyles, type HomeSurface } from './homeShared';

/**
 * Door styles as shop cards: a drawn door on a warm stage, the style's name, a
 * row of colour dots, a one-line note and a from-price.
 *
 * <p>Each card is one link. The drawing is decorative (the name says which
 * style it is); the colour dots are decorative too, and a visually hidden
 * "N colours shown" tells a screen-reader user what the dots are. The
 * from-price is written by the owner and has no default.
 *
 * <p>Three columns from 1024 px, two from 640 px, one below.
 */
export interface SpatialKitchenStyleCard {
  style?: string;
  colour?: string;
  name?: Localized;
  note?: Localized;
  /** Up to six `#rrggbb` colours the style comes in. */
  swatches?: string[];
  /** A from-price, written out: "from £24". */
  fromLabel?: Localized;
  href?: string;
}

export interface SpatialKitchenStyleCardsProps {
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  seeAllLabel?: Localized;
  seeAllHref?: string;
  cards?: SpatialKitchenStyleCard[];
  /** Visually hidden count of the colour dots; `{count}` is replaced by the number. */
  coloursShownLabel?: Localized;
  surface?: HomeSurface;
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-stylecards__head{display:flex;flex-wrap:wrap;justify-content:space-between;align-items:end;gap:1rem 24px;margin-bottom:clamp(2rem,1.5rem + 2vw,2.5rem)}
.sk-stylecards__head .sk-section-head{margin-bottom:0}
.sk-stylecards__grid{display:grid;gap:22px;margin:0;padding:0;list-style:none}
@media (min-width:640px){.sk-stylecards__grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (min-width:1024px){.sk-stylecards__grid{grid-template-columns:repeat(3,minmax(0,1fr))}}
.sk-stylecards__grid > li{display:flex;min-width:0}
.sk-stylecard{display:flex;flex-direction:column;width:100%;overflow:hidden;background:var(--sk-white);border:1px solid var(--sk-border);border-radius:var(--sk-radius-card);color:var(--sk-ink);text-decoration:none;transition:box-shadow .2s,transform .2s}
.sk-stylecard:hover{box-shadow:var(--sk-shadow-lg);transform:translateY(-3px)}
@media (prefers-reduced-motion:reduce){.sk-stylecard:hover{transform:none}}
.sk-stylecard__stage{display:grid;place-items:center;height:230px;background:linear-gradient(180deg,#efe7da,#e6dccc)}
.sk-stylecard__stage .sk-art{display:block;height:190px}
.sk-stylecard__stage svg{display:block;height:100%;width:auto;filter:drop-shadow(0 10px 14px rgba(0,0,0,.14))}
.sk-stylecard__body{flex:1;padding:18px 20px 20px}
.sk-stylecard__name{font-size:1.15rem}
.sk-stylecard__chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:12px}
.sk-stylecard__chip{width:18px;height:18px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 0 1px var(--sk-stone-deep)}
.sk-stylecard__meta{display:flex;flex-wrap:wrap;justify-content:space-between;align-items:center;gap:.25rem 1rem;margin-top:10px;font-size:.88rem;color:var(--sk-faint)}
.sk-stylecard__from{font-weight:700;color:var(--sk-ink)}
`;

function StyleCard({
  card,
  TitleTag,
  coloursShown,
}: {
  card: SpatialKitchenStyleCard;
  TitleTag: 'h2' | 'h3' | 'h4';
  coloursShown: string;
}) {
  const artId = useId();
  const swatches = (Array.isArray(card.swatches) ? card.swatches : [])
    .map(hexOrUndefined)
    .filter((hex): hex is string => Boolean(hex))
    .slice(0, 6);
  const note = text(card.note);
  const fromLabel = text(card.fromLabel);
  return (
    <SkLink href={card.href} className="sk-stylecard">
      <div className="sk-stylecard__stage" aria-hidden="true">
        <SvgArt svg={doorSvg(doorStyleOr(card.style), safeHex(card.colour), { idPrefix: artId })} />
      </div>
      <div className="sk-stylecard__body">
        <TitleTag className="sk-heading sk-stylecard__name">{text(card.name)}</TitleTag>
        {swatches.length > 0 && (
          <>
            <span className="sk-stylecard__chips" aria-hidden="true">
              {swatches.map((hex, index) => (
                <i key={`${hex}-${index}`} className="sk-stylecard__chip" style={{ background: hex }} />
              ))}
            </span>
            <span className="sk-visually-hidden">{coloursShown.replace('{count}', String(swatches.length))}</span>
          </>
        )}
        {(note || fromLabel) && (
          <div className="sk-stylecard__meta">
            {note && <span>{note}</span>}
            {fromLabel && <span className="sk-stylecard__from">{fromLabel}</span>}
          </div>
        )}
      </div>
    </SkLink>
  );
}

export function SpatialKitchenStyleCards(props: SpatialKitchenStyleCardsProps) {
  const cards = items<SpatialKitchenStyleCard>(props.cards).filter((card) => text(card.name));
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const CardTitle = childLevel(level);
  const titleId = useId();
  const seeAllLabel = text(props.seeAllLabel);
  const coloursShown = text(props.coloursShownLabel, '{count} colours shown');

  return (
    <BlockRoot
      as="section"
      className={`sk sk-stylecards sk-section ${homeSurfaceClass(props.surface, 'warm')}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <HomeSurfaceStyles />
      <SkStyles id="style-cards" css={CSS} />
      <div className="sk-container">
        <div className="sk-stylecards__head">
          <div className="sk-section-head">
            {text(props.eyebrow) && <p className="sk-eyebrow">{text(props.eyebrow)}</p>}
            <Title id={titleId} className="sk-heading sk-h2">
              {text(props.title, 'Door styles')}
            </Title>
            {text(props.intro) && <p className="sk-lead">{text(props.intro)}</p>}
          </div>
          {seeAllLabel && props.seeAllHref && (
            <SkLink href={props.seeAllHref} className="sk-link-arrow">
              {seeAllLabel}
            </SkLink>
          )}
        </div>
        <ul className="sk-stylecards__grid">
          {cards.map((card, index) => (
            <li key={`${text(card.name)}-${index}`}>
              <StyleCard card={card} TitleTag={CardTitle} coloursShown={coloursShown} />
            </li>
          ))}
        </ul>
      </div>
    </BlockRoot>
  );
}
